import { useAchievements } from "api/achievements";
import { useAppSelector } from "app/hooks";
import { useMessage } from "hooks/intl";
import moment from "moment";
import { selectDefaultLanguageData } from "reduxState/translations/translationsSlice";
import { DifferencesInRule, Rule } from "types/members/timeline";
import { formatNumberValue } from "utilities/numbers";

// timeline begin
export const findDifferences = (
  rulesBeforeChanges?: Rule[],
  rulesAfterChanges?: Rule[],
): DifferencesInRule[] | undefined => {
  // Return undefined if either of the input arrays is missing
  if (!rulesBeforeChanges || !rulesAfterChanges) {
    return undefined;
  }

  // Create a map of achievementRuleId and currentPeriodValue from the first array
  const map1 = new Map(
    rulesBeforeChanges.map((item) => [
      item.achievementRuleId,
      item.currentPeriodValue,
    ]),
  );

  // Iterate over the second array and compare currentPeriodValues
  const differences = rulesAfterChanges
    .map((item) => {
      const previousPeriodValue = map1.get(item.achievementRuleId);
      if (
        previousPeriodValue !== undefined &&
        previousPeriodValue !== item.currentPeriodValue
      ) {
        // Return the object with differences if currentPeriodValue has changed
        return {
          achievementRuleId: item.achievementRuleId,
          previousPeriodValue,
          currentPeriodValue: item.currentPeriodValue,
        };
      }
      return null; // Ignore if there's no change or if the item doesn't exist in the first array
    })
    .filter((diff): diff is NonNullable<typeof diff> => diff !== null); // Remove null values

  // Return the array of differences, or undefined if there are no changes
  return differences.length > 0 ? differences : undefined;
};

export const RulesText = ({
  rules,
  achievementId,
}: {
  rules?: DifferencesInRule[];
  achievementId?: string;
}) => {
  const { message } = useMessage();
  const { useAchievementData } = useAchievements();
  const { data: achievementData } = useAchievementData(achievementId);
  const defaultLang = useAppSelector(selectDefaultLanguageData);
  const { code } = defaultLang;
  const findAchievementRuleName = (
    achievementRuleId: string,
    index: number,
  ) => {
    const element = achievementData?.rules?.find(
      (item) => item?.achievementRuleId === achievementRuleId,
    );

    return (
      element?.translations?.[code]?.name ||
      message({ id: "ruleNumber" }, { number: index + 1 })
    );
  };

  const copy = rules
    ?.map((rule, index) =>
      message(
        { id: "achievementProgressionRulesText" },
        {
          ruleName: findAchievementRuleName(rule?.achievementRuleId, index),
          previousPeriodValue: formatNumberValue(rule?.previousPeriodValue),
          currentPeriodValue: formatNumberValue(rule?.currentPeriodValue),
        },
      ),
    )
    .join(", ");

  return copy;
};

// timeline end

export const calculateDaysUntilNextBirthDate = (birthDate: string) => {
  const currentDate = moment().startOf("day");
  const birthDateMoment = moment(birthDate).startOf("day");
  const thisYearBirthDate = birthDateMoment.clone().set({
    year: currentDate.year(),
  });

  // Check if the birthday is today
  if (thisYearBirthDate.isSame(currentDate, "day")) {
    return 0;
  }
  // Check if this year's birthday has already passed
  const isBirthdayPassed = thisYearBirthDate.isBefore(currentDate);

  const nextBirthday = isBirthdayPassed
    ? thisYearBirthDate.clone().add(1, "year")
    : thisYearBirthDate;
  // Calculate days until the next birthday
  const daysUntilNextBirthday = nextBirthday.diff(currentDate, "days");

  return Math.max(daysUntilNextBirthday, 1);
};
